/**
 * Carrinho do tema novo.
 *
 * Toda escrita passa por Shopify.actions.updateCart (Storefront API): tema,
 * apps e agentes usam o mesmo caminho e emitem os mesmos eventos padrão
 * (shopify:cart:lines-update etc.). Depois de cada escrita, o handler
 * configurado no layout re-renderiza, numa só chamada à Section Rendering
 * API, cada carrinho da página: a gaveta e, em /cart, a seção da página.
 * Exceção: com um combo (bundle) no carrinho, quantidade e remoção de
 * qualquer linha vão pela Ajax API (changeBundleLine).
 *
 * Cada carrinho é uma raiz [data-e-cart-root] com o id da seção em
 * [data-e-cart-section]; só o [data-e-cart-body] dela é trocado. Aviso,
 * erro e foco ficam na raiz onde a pessoa agiu. Na página do carrinho, a
 * gaveta não abre: "abrir o carrinho" leva o foco à própria página.
 *
 * O cupom usa o mesmo caminho: updateCart com discountCodes (o conjunto
 * inteiro de códigos). Código que não vale para o carrinho sai numa segunda
 * chamada, e o erro fica no campo.
 *
 * Sem Shopify.actions (runtime indisponível), os formulários seguem o envio
 * nativo (/cart/add no produto, /cart/change nos controles da linha,
 * /cart/update no cupom) e o link do carrinho abre /cart.
 */

const UPDATE_ERROR = 'Não foi possível atualizar o carrinho. Tente de novo.';
const COUPON_INVALID = 'Esse cupom não vale para este carrinho.';
const SAVING_COUNT_MS = 600;
let activeForm = null;
let activeRoot = null;
let lastAddedVariant = null;
let couponDraft = null;

const drawerElement = () => document.querySelector('e-cart-drawer');
const cartPage = () => document.querySelector('[data-e-cart-page]');
const hasActions = () => Boolean(window.Shopify && window.Shopify.actions);
const cartPath = () => `${window.Shopify?.routes?.root ?? '/'}cart`;
const cartContext = (root) => (root === drawerElement() ? 'dialog' : 'cart');
const findLine = (scope, key) => [...scope.querySelectorAll('[data-line-key]')].find((line) => line.dataset.lineKey === key);
const isBusy = (root) => root.querySelector('[data-e-cart-body]')?.getAttribute('aria-busy') === 'true';

function setBodyBusy(root, busy) {
  const body = root.querySelector('[data-e-cart-body]');
  if (busy) body?.setAttribute('aria-busy', 'true');
  else body?.removeAttribute('aria-busy');
}

function variantGid(id) {
  const value = String(id);
  return value.startsWith('gid://') ? value : `gid://shopify/ProductVariant/${value}`;
}

/* O e-pdp.js observa o texto do [data-e-cart-count] para esconder o aviso
   "Adicionado" quando o carrinho muda (também pelo changeBundleLine, sem
   evento): o número continua no textContent. */
function updateCounts(totalQuantity) {
  const label = totalQuantity === 1 ? 'Carrinho, 1 item' : `Carrinho, ${totalQuantity} itens`;
  document.querySelectorAll('[data-e-cart-count]').forEach((element) => {
    element.textContent = String(totalQuantity);
    element.hidden = totalQuantity === 0;
  });
  document.querySelectorAll('[data-e-cart-link]').forEach((element) => element.setAttribute('aria-label', label));
}

/* Trocar o corpo da gaveta derruba o foco no <body>. Antes da troca, anota
   onde ele estava (linha, papel do controle e posição da linha); depois,
   devolve ao mesmo controle. Se a linha saiu, vai para o nome da linha que
   ficou no lugar (ou da anterior); com o carrinho vazio, para o aviso. */
function focusedLine(body) {
  const active = document.activeElement;
  const line = active && body.contains(active) ? active.closest('[data-line-key]') : null;
  if (!line) return null;
  const lines = [...body.querySelectorAll('[data-line-key]')];
  return {
    key: line.dataset.lineKey,
    role: active.dataset.eQtyRole,
    index: lines.indexOf(line),
    count: lines.length,
  };
}

/* A chave muda com os descontos (com cupom, a cada troca de quantidade).
   Sem ela e sem linha removida, vale a única linha da mesma variante (a
   chave começa por "<variant_id>:") ou, havendo mais de uma, a da mesma
   posição. */
function lineAfterSwap(body, target) {
  const lines = [...body.querySelectorAll('[data-line-key]')];
  const exact = findLine(body, target.key);
  if (exact || lines.length !== target.count) return exact;
  const variant = `${target.key.split(':')[0]}:`;
  const sameVariant = lines.filter((line) => line.dataset.lineKey.startsWith(variant));
  return sameVariant.length === 1 ? sameVariant[0] : lines[target.index];
}

function restoreFocus(root, body, target) {
  if (!target) return;
  const lines = [...body.querySelectorAll('[data-line-key]')];
  const sameLine = lineAfterSwap(body, target);
  const element =
    (target.role && sameLine?.querySelector(`[data-e-qty-role="${target.role}"]`)) ||
    (lines[target.index] ?? lines[target.index - 1])?.querySelector('.e-cart__name') ||
    body.querySelector('[data-e-cart-empty]') ||
    root.querySelector('[data-e-cart-close]');
  element?.focus();
}

function announce(root, message) {
  const status = root?.querySelector('[data-e-cart-status]');
  if (status) status.textContent = message;
}

/* Com o id da linha, o erro vai para a própria linha; sem ela (a linha
   saiu ou a chave mudou), para o aviso geral da raiz. */
function showCartError(root, key, message) {
  const element =
    (key && findLine(root, key)?.querySelector('[data-e-cart-line-error]')) ||
    root.querySelector('[data-e-cart-error]');
  showError(element, message);
}

async function renderCarts() {
  const roots = [...document.querySelectorAll('[data-e-cart-root][data-e-cart-section]')];
  if (!roots.length) return;
  const ids = roots.map((root) => root.dataset.eCartSection);
  const sections = await fetchSections(ids);
  roots.forEach((root) => replaceBody(root, sections[root.dataset.eCartSection]));
}

async function fetchSections(ids) {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('sections', ids.join(','));
  /* Template alternativo (?view=): a seção da página do carrinho só existe nele. */
  const view = new URLSearchParams(window.location.search).get('view');
  if (view) url.searchParams.set('view', view);
  /* Tema em pré-visualização: depois de uma escrita pela Storefront API, ?sections= pode
     responder com o tema publicado (seção null). O preview_theme_id mantém o tema certo;
     com o tema publicado (role "main"), o parâmetro não entra. O proxy do
     `shopify theme dev` responde 502 com esse parâmetro (25/09/2026): aí vale a
     segunda tentativa, sem ele. */
  const attempts = [url];
  const theme = window.Shopify?.theme;
  if (theme?.id && theme.role !== 'main') {
    const preview = new URL(url);
    preview.searchParams.set('preview_theme_id', theme.id);
    attempts.unshift(preview);
  }
  for (const attempt of attempts) {
    /* Sem "Accept: application/json": com ele, a URL de produto devolve o JSON do produto
       e /cart devolve o cart.js, no lugar das seções. A resposta de ?sections= já é JSON. */
    const response = await fetch(attempt).catch(() => null);
    if (!response?.ok) continue;
    const sections = await response.json().catch(() => null);
    if (sections && ids.every((id) => sections[id])) return sections;
  }
  throw new Error('Section Rendering API sem as seções do carrinho');
}

function replaceBody(root, html) {
  if (!html) return;
  const section = new DOMParser().parseFromString(html, 'text/html');
  const fresh = section.querySelector('[data-e-cart-body]');
  const current = root.querySelector('[data-e-cart-body]');
  if (!fresh || !current) return;
  const target = focusedLine(current);
  const coupon = couponDraft?.root === root ? couponDraft : openCoupon(current);
  current.replaceWith(fresh);
  if (coupon) paintCoupon(root, coupon);
  /* O <s-view-event> do carrinho fica fora do corpo: recriado com a gaveta
     aberta, dispararia outro shopify:cart:view. Só o payload muda, e o
     elemento o lê na hora de disparar. */
  const payload = section.querySelector('[data-e-cart-view]')?.getAttribute('view-event-payload');
  if (payload) root.querySelector('[data-e-cart-view]')?.setAttribute('view-event-payload', payload);
  /* Cards do carrinho vazio: a revelação no scroll (e-core.js) só observa o que já existia. */
  fresh.querySelectorAll('.e-reveal').forEach((element) => element.classList.add('is-in'));
  if (root === drawerElement()) root.observeFoot();
  restoreFocus(root, fresh, target);
}

function showError(element, message) {
  if (!element) return;
  element.textContent = message;
  element.hidden = false;
}

/* Na página do carrinho, "abrir o carrinho" (depois de adicionar ou pelo
   ícone do cabeçalho) leva o foco à linha recém-adicionada ou ao título. */
function focusCartPage(page) {
  const added = lastAddedVariant
    ? [...page.querySelectorAll('[data-line-key]')].find((line) => line.dataset.lineKey.startsWith(`${lastAddedVariant}:`))
    : null;
  lastAddedVariant = null;
  if (added) announce(page, `Adicionado ao carrinho: ${added.dataset.eLineTitle}.`);
  (added?.querySelector('.e-cart__name') ?? page.querySelector('[data-e-cart-heading]'))?.focus();
}

function setBusy(button, busy) {
  if (!button) return;
  const label = button.querySelector('[data-e-label]');
  if (busy) {
    button.dataset.eIdleLabel = label?.textContent ?? '';
    if (label && button.dataset.eBusyLabel) label.textContent = button.dataset.eBusyLabel;
  } else if (label && button.dataset.eIdleLabel !== undefined) {
    label.textContent = button.dataset.eIdleLabel;
  }
  /* Só aria-busy, sem "disabled": botão desabilitado perde o foco para o
     <body>, e o <dialog> da gaveta, ao fechar, devolvia o foco para lá. O
     segundo clique é barrado no envio. */
  button.setAttribute('aria-busy', String(busy));
}

/* ---------- Gaveta ---------- */
class ECartDrawer extends HTMLElement {
  opener = null;

  /* Altura do rodapé fixo (muda com desconto e erro) para o scroll-padding
     do painel em e-cart.css: o controle focado pelo Tab não fica atrás dele. */
  footObserver = new ResizeObserver(([entry]) => {
    this.style.setProperty('--e-cart-foot-h', `${Math.ceil(entry.borderBoxSize[0].blockSize)}px`);
  });

  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-e-cart-close]')) this.close();
    });
    this.dialog?.addEventListener('close', () => this.returnFocus());
    this.observeFoot();
  }

  get dialog() {
    return this.querySelector('dialog');
  }

  /* O rodapé é recriado a cada troca do corpo: volta a observar o novo. Com
     a gaveta aberta, a altura entra já na troca: o foco devolvido logo
     depois (antes da primeira medida do observer) rolaria com a altura do
     rodapé antigo. Fechada, a medida é 0 e fica para o observer. */
  observeFoot() {
    this.footObserver.disconnect();
    const foot = this.querySelector('.e-cart__foot');
    if (!foot) return;
    if (foot.offsetHeight) this.style.setProperty('--e-cart-foot-h', `${foot.offsetHeight}px`);
    this.footObserver.observe(foot);
  }

  open() {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;
    this.opener = document.activeElement;
    dialog.showModal();
  }

  close() {
    this.dialog?.close();
  }

  /* O <dialog> devolve o foco a quem estava focado no showModal(). Se esse
     elemento saiu da página ou o foco caiu no <body>, devolve à mão. */
  returnFocus() {
    const opener = this.opener;
    this.opener = null;
    const active = document.activeElement;
    const lost = !active || active === document.body || this.contains(active);
    if (lost && opener?.isConnected && opener !== document.body) opener.focus();
  }
}
if (!customElements.get('e-cart-drawer')) customElements.define('e-cart-drawer', ECartDrawer);

/* ---------- Integração com Shopify.actions (chamada pelo layout) ---------- */
window.eternitaCart = {
  eventTarget(meta) {
    if (meta.type === 'shopify:cart:lines-update' && meta.action === 'add' && activeForm?.isConnected) {
      return activeForm;
    }
    if (activeRoot?.isConnected) return activeRoot;
    return cartPage() ?? drawerElement() ?? null;
  },

  async handleUpdate(defaultHandler) {
    const result = await defaultHandler();
    if (result?.cart) updateCounts(result.cart.totalQuantity);
    try {
      await renderCarts();
    } catch {
      /* A escrita valeu, mas a tela ficou com o carrinho antigo (e o aviso
         anunciaria uma quantidade que ela não mostra): recarregar, como faz
         o padrão da própria Shopify quando não consegue re-renderizar. */
      if (!result?.userErrors?.length) window.location.reload();
    }
    return { ...result, detail: { ...(result?.detail ?? {}), handledBy: 'theme' } };
  },

  async open() {
    const page = cartPage();
    if (page) {
      focusCartPage(page);
      return;
    }
    const drawer = drawerElement();
    if (drawer) drawer.open();
    else window.location.assign(cartPath());
  },
};

/* ---------- Formulários de produto ---------- */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-e-product-form]');
  if (!form || !hasActions()) return;
  event.preventDefault();

  const button = form.querySelector('[type="submit"]');
  if (button?.getAttribute('aria-busy') === 'true') return;
  const error = form.querySelector('[data-e-form-error]');
  const data = new FormData(form);
  if (error) error.hidden = true;
  activeForm = form;
  setBusy(button, true);

  let result;
  try {
    result = await window.Shopify.actions.updateCart(
      { lines: [{ merchandiseId: variantGid(data.get('id')), quantity: Number(data.get('quantity')) || 1 }] },
      { event: { context: 'product' } },
    );
  } catch (failure) {
    if (failure?.name !== 'AbortError') form.submit(); /* rede ou runtime falhou: o envio nativo garante a compra */
    return;
  } finally {
    activeForm = null;
    setBusy(button, false);
  }
  if (result.userErrors?.length) {
    showError(error, result.userErrors[0].message);
    return;
  }
  lastAddedVariant = String(data.get('id'));
  await openAfterAdd(result.warnings?.[0]?.message);
});

/* Fora do try do updateCart: o item já entrou, e o envio nativo o
   adicionaria de novo. Se abrir falhar, vai para a página do carrinho. O
   aviso que não bloqueia (ex.: entrou menos do que o pedido, por estoque)
   aparece no carrinho aberto. */
async function openAfterAdd(warning) {
  try {
    await window.Shopify.actions.openCart();
  } catch {
    window.location.assign(cartPath());
    return;
  }
  if (warning) showError((cartPage() ?? drawerElement())?.querySelector('[data-e-cart-error]'), warning);
}

/* ---------- Quantidade e remoção (gaveta e página) ---------- */
/* Linha de combo (bundle, marcada no Liquid com data-e-line-bundle): na
   Storefront API ela é uma ComponentizableCartLine, e o updateCart não a
   encontra pela chave ("No cart line matches this viewKey."; a linha comum
   aceita a mesma chave). Por isso ela vai pela Ajax API, que aceita a chave, e
   depois re-renderiza os carrinhos como o handleUpdate, também no erro (a
   chave pode ter ficado velha). Com um combo no carrinho, a linha comum vai
   por aqui também: o updateCart manda a chave como viewKey ao cartLinesRemove,
   e a Storefront API tirou o combo (que tem a mesma variante como
   componente) no lugar do avulso, 2 de 2 vezes depois de tirar o cupom
   (ALRIX, 26/09/2026). A Ajax API acha a linha pela chave. Por este caminho
   não sai o shopify:cart:lines-update. O erro da Ajax API ora vem em
   português, ora em inglês ("Cannot find variant", com chave velha): vale o
   aviso do tema. */
async function changeBundleLine(key, quantity) {
  const response = await fetch(`${cartPath()}/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: key, quantity }),
  });
  if (response.ok) updateCounts((await response.json()).item_count);
  try {
    await renderCarts();
  } catch {
    if (response.ok) window.location.reload();
  }
  return { userErrors: response.ok ? [] : [{ message: UPDATE_ERROR }] };
}

document.addEventListener('click', async (event) => {
  const opener = event.target.closest('[data-e-cart-open]');
  if (opener && hasActions() && (cartPage() || drawerElement())) {
    event.preventDefault();
    window.Shopify.actions.openCart();
    return;
  }

  const couponToggle = event.target.closest('[data-e-coupon-toggle]');
  if (couponToggle) {
    toggleCoupon(couponToggle);
    return;
  }

  const quantityButton = event.target.closest('[data-line-key] button[name="quantity"]');
  if (!quantityButton || !hasActions()) return;
  const line = quantityButton.closest('[data-line-key]');
  const root = quantityButton.closest('[data-e-cart-root]');
  if (!root) return;
  /* O <form> da linha (POST /cart/change) é só para quem está sem JS. */
  event.preventDefault();
  /* Enquanto a troca anterior não volta, os números da tela são os antigos:
     um segundo clique (ou Enter) mandaria a mesma quantidade de novo. */
  if (isBusy(root)) return;

  const key = line.dataset.lineKey;
  const quantity = Number(quantityButton.value);
  const name = line.dataset.eLineTitle;
  root.querySelectorAll('[data-e-cart-line-error], [data-e-cart-error]').forEach((element) => {
    element.hidden = true;
  });
  setBodyBusy(root, true);
  activeRoot = root;

  try {
    const { userErrors, warnings } = root.querySelector('[data-e-line-bundle]')
      ? await changeBundleLine(key, quantity)
      : await window.Shopify.actions.updateCart({ lines: [{ id: key, quantity }] }, { event: { context: cartContext(root) } });
    const message = userErrors?.[0]?.message ?? warnings?.[0]?.message;
    if (message) showCartError(root, key, message);
    else announce(root, quantity === 0 ? `Removido do carrinho: ${name}.` : `Quantidade de ${name}: ${quantity}.`);
  } catch {
    showCartError(root, key, UPDATE_ERROR);
  } finally {
    activeRoot = null;
    setBodyBusy(root, false);
  }
});

/* ---------- Cupom (gaveta e página) ---------- */
/* Os <form> do cupom (POST /cart/update) são só para quem está sem JS. */
document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-e-coupon-form], [data-e-coupon-remove]');
  if (!form || !hasActions()) return;
  event.preventDefault();
  if (form.matches('[data-e-coupon-form]')) applyCoupon(form);
  else removeCoupon(form);
});

const sameCode = (first, second) => first.toUpperCase() === second.toUpperCase();
const savingOf = (root) => Number(root.querySelector('[data-e-cart-saving]')?.dataset.eCartSaving ?? 0);

async function discountCodes() {
  const { cart } = await window.Shopify.actions.getCart();
  return (cart?.discountCodes ?? []).map((discount) => discount.code);
}

function updateDiscountCodes(root, codes) {
  return window.Shopify.actions.updateCart({ discountCodes: codes }, { event: { context: cartContext(root) } });
}

function setCouponOpen(coupon, open) {
  coupon.classList.toggle('is-open', open);
  coupon.querySelector('[data-e-coupon-toggle]')?.setAttribute('aria-expanded', String(open));
}

/* Rolagem explícita depois do foco: o scrollIntoView respeita o
   scroll-padding do painel e deixa o alvo (e o erro do campo) acima do
   rodapé fixo da gaveta; na página, só rola se o alvo estiver fora da tela. */
function focusInView(element, area = element) {
  element?.focus({ preventScroll: true });
  area?.scrollIntoView({ block: 'nearest' });
}

function focusCouponField(coupon) {
  focusInView(coupon?.querySelector('[data-e-coupon-input]'), coupon?.querySelector('[data-e-coupon-form]'));
}

function toggleCoupon(toggle) {
  const coupon = toggle.closest('[data-e-coupon]');
  const open = !coupon.classList.contains('is-open');
  setCouponOpen(coupon, open);
  if (open) focusCouponField(coupon);
}

/* Campo aberto (com o que foi digitado) continua aberto depois da troca do corpo. */
function openCoupon(body) {
  const coupon = body.querySelector('[data-e-coupon].is-open');
  return coupon ? { value: coupon.querySelector('[data-e-coupon-input]').value } : null;
}

/* Estado do campo no corpo novo: aberto, valor, "Aplicando…" e erro. */
function paintCoupon(root, { value, busy = false, error = '' }) {
  const coupon = root.querySelector('[data-e-coupon]');
  if (!coupon) return;
  const input = coupon.querySelector('[data-e-coupon-input]');
  const message = coupon.querySelector('[data-e-coupon-error]');
  setCouponOpen(coupon, true);
  input.value = value;
  setBusy(coupon.querySelector('[data-e-coupon-form] [type="submit"]'), busy);
  message.textContent = error;
  message.hidden = !error;
  if (error) {
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', message.id);
  } else {
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
  }
}

/* O código entra junto dos que já estão no carrinho. Se não vale (applicable
   false ou erro), uma segunda chamada o tira, e o campo mostra o erro sem
   inventar motivo. O corpo é trocado a cada chamada: couponDraft mantém o
   campo aberto, com o código e o "Aplicando…", entre as trocas. */
async function applyCoupon(form) {
  const root = form.closest('[data-e-cart-root]');
  const input = form.querySelector('[data-e-coupon-input]');
  const code = input.value.trim();
  if (!root || isBusy(root)) return;
  if (!code) {
    input.focus();
    return;
  }
  const savingBefore = savingOf(root);
  const error = root.querySelector('[data-e-cart-error]');
  if (error) error.hidden = true;
  couponDraft = { root, value: code, busy: true };
  paintCoupon(root, couponDraft);
  setBodyBusy(root, true);
  activeRoot = root;

  let applied = false;
  try {
    const others = (await discountCodes()).filter((other) => !sameCode(other, code));
    const result = await updateDiscountCodes(root, [...others, code]);
    applied =
      !result.userErrors?.length &&
      Boolean(result.cart?.discountCodes?.some((discount) => discount.applicable && sameCode(discount.code, code)));
    if (!applied) {
      setBodyBusy(root, true);
      await updateDiscountCodes(root, others);
    }
    couponDraft = applied ? null : { root, value: code, error: COUPON_INVALID };
  } catch {
    couponDraft = { root, value: code, error: UPDATE_ERROR };
  } finally {
    activeRoot = null;
    setBodyBusy(root, false);
  }

  if (applied) {
    celebrateCoupon(root, code, savingBefore);
    return;
  }
  paintCoupon(root, couponDraft);
  couponDraft = null;
  focusCouponField(root.querySelector('[data-e-coupon]'));
}

async function removeCoupon(form) {
  const root = form.closest('[data-e-cart-root]');
  const code = form.closest('[data-e-coupon-code]')?.dataset.eCouponCode;
  if (!root || !code || isBusy(root)) return;
  const error = root.querySelector('[data-e-cart-error]');
  if (error) error.hidden = true;
  setBodyBusy(root, true);
  activeRoot = root;
  try {
    const codes = (await discountCodes()).filter((other) => !sameCode(other, code));
    const { userErrors } = await updateDiscountCodes(root, codes);
    if (userErrors?.length) showCartError(root, null, userErrors[0].message);
    else announce(root, `Cupom ${code} removido.`);
  } catch {
    showCartError(root, null, UPDATE_ERROR);
  } finally {
    activeRoot = null;
    setBodyBusy(root, false);
  }
  focusInView(root.querySelector('[data-e-coupon-toggle]'));
}

/* Cupom aplicado: campo fechado, aviso com o valor do selo (sem selo, só o
   nome: nada de valor inventado), foco em "Tem outro cupom?". Com movimento
   liberado, o selo novo brilha uma vez e a faixa "Você economiza" conta do
   valor antigo até o novo. */
function celebrateCoupon(root, code, savingBefore) {
  const coupon = root.querySelector('[data-e-coupon]');
  if (coupon) {
    setCouponOpen(coupon, false);
    coupon.querySelector('[data-e-coupon-input]').value = '';
    setBusy(coupon.querySelector('[data-e-coupon-form] [type="submit"]'), false);
  }
  const chip = [...root.querySelectorAll('[data-e-coupon-code]')].find((element) => sameCode(element.dataset.eCouponCode, code));
  announce(
    root,
    chip ? `Cupom ${chip.dataset.eCouponCode} aplicado. Você economiza ${chip.dataset.eCouponAmount}.` : `Cupom ${code} aplicado.`,
  );
  focusInView(root.querySelector('[data-e-coupon-toggle]'));
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  chip?.addEventListener('animationend', () => chip.classList.remove('is-new'), { once: true });
  chip?.classList.add('is-new');
  countSaving(root.querySelector('[data-e-cart-saving]'), savingBefore);
}

/* A contagem usa o Intl na moeda da loja; o último quadro devolve o texto do
   filtro money, o mesmo de todo o carrinho. */
function countSaving(band, from) {
  const value = band?.querySelector('[data-e-cart-saving-value]');
  const to = Number(band?.dataset.eCartSaving);
  const currency = window.Shopify?.currency?.active;
  if (!value || !currency || !(to > from)) return;
  const final = value.textContent;
  const money = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency });
  const start = performance.now();
  const frame = (now) => {
    if (!value.isConnected) return;
    const progress = Math.min((now - start) / SAVING_COUNT_MS, 1);
    const eased = 1 - (1 - progress) ** 3;
    value.textContent = progress < 1 ? money.format((from + (to - from) * eased) / 100) : final;
    if (progress < 1) requestAnimationFrame(frame);
  };
  value.textContent = money.format(from / 100);
  requestAnimationFrame(frame);
}
